import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, type DataTableColumn, type FilterPill } from '@/components/shared/DataTable';
import PageHeader from '@/components/vf/PageHeader';
import { useOrganisations } from '@/hooks/queries/useAdmin';
import { useCursorPages } from '@/hooks/useCursorPages';
import {
  listAuditLogPage, listAuditLogActions, downloadAuditLogCsv,
  type AuditLogItem, type AuditLogFilters,
} from '@/api/endpoints/backoffice';
import { showErrorToast, toUserMessage } from '@/lib/errorToast';
import { ApiErrorException } from '@/api/client';

/**
 * /AdminAuditLogs — the audit trail, super admin only (W6, B3-AdminAuditLogs).
 *
 *   - French labels for the actions (the technical code stays beside, small);
 *   - the detail of a row folds open: before/after values side by side,
 *     then the other metadata, ids and user agent (no raw JSON in the row);
 *   - facets: action (from the journal itself), actor (start of the email),
 *     organisation (acting or acted upon), period; kept in the URL, so the
 *     organisation row links here (`?organisation_id=`);
 *   - server cursor pages with the total; CSV export of the filter (the only
 *     audit export, SR3-5).
 */

const PAGE_SIZE = 50;
const PERIODS = ['24h', '7d', '30d', '90d', 'all', 'custom'] as const;
type Period = (typeof PERIODS)[number];
const PERIOD_MS: Record<string, number> = { '24h': 864e5, '7d': 7 * 864e5, '30d': 30 * 864e5, '90d': 90 * 864e5 };
const ALL = '__all__';

/** `membership.role_change` → « Rôle modifié »; `prospectqueue.delete` → « Suppression : prospectqueue ». */
export function actionLabel(t: TFunction, action: string): string {
  const key = `admin:auditLogs.actions.${action.replace(/\./g, '_')}`;
  if (t(key, { defaultValue: '' })) return t(key);
  const del = /^([a-z]+)\.delete$/i.exec(action);
  if (del) return t('admin:auditLogs.actions.entityDelete', { entity: del[1] });
  return action;
}

const DESTRUCTIVE = /(delete|remove|reset)/;

function periodRange(period: Period, from: string, to: string, now: number): { from?: string; to?: string } {
  if (period === 'all') return {};
  if (period === 'custom') {
    return {
      from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      // `to` is a day, inclusive for the user: the server bound is the next midnight.
      to: to ? new Date(new Date(`${to}T00:00:00`).getTime() + 864e5).toISOString() : undefined,
    };
  }
  return { from: new Date(now - (PERIOD_MS[period] ?? 0)).toISOString() };
}

export default function AdminAuditLogs() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const [params, setParams] = useSearchParams();
  const action = params.get('action') ?? '';
  const organisationId = params.get('organisation_id') ?? '';
  const period = (PERIODS as readonly string[]).includes(params.get('period') ?? '') ? (params.get('period') as Period) : '30d';
  const customFrom = params.get('from') ?? '';
  const customTo = params.get('to') ?? '';
  const [actorDraft, setActorDraft] = useState(params.get('actor') ?? '');
  const actor = params.get('actor') ?? '';
  // The period bound is fixed when the period is picked, not at each render.
  const [now, setNow] = useState(() => Date.now());

  // REV-W6-F4: from the latest URL, not the render's: the actor debounce
  // fires 350 ms later and used to write back the facets of that render.
  // (react-router's functional form reads the same stale params, hence a ref.)
  const latestParams = useRef(params);
  latestParams.current = params;
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(latestParams.current);
    if (value) next.set(key, value); else next.delete(key);
    latestParams.current = next;
    setParams(next, { replace: true });
  };

  // Typing in the actor box filters after a short pause.
  useEffect(() => {
    if (actorDraft.trim() === actor) return;
    const id = setTimeout(() => setParam('actor', actorDraft.trim()), 350);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorDraft]);

  const filters: AuditLogFilters = useMemo(() => ({
    action: action || undefined,
    actor: actor || undefined,
    organisation_id: organisationId || undefined,
    ...periodRange(period, customFrom, customTo, now),
  }), [action, actor, organisationId, period, customFrom, customTo, now]);

  const { query, page, onPageChange } = useCursorPages(
    ['admin', 'audit-logs', filters],
    (cursor, signal) => listAuditLogPage(filters, { limit: PAGE_SIZE, cursor }, signal),
  );
  const actions = useQuery({ queryKey: ['admin', 'audit-logs', 'facets'], queryFn: ({ signal }) => listAuditLogActions(signal), staleTime: 60_000 });
  const orgs = useOrganisations();
  const orgName = useMemo(() => new Map((orgs.data?.organisations ?? []).map((o) => [o.id, o.company_name])), [orgs.data]);
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { truncated } = await downloadAuditLogCsv(filters);
      if (truncated) toast.warning(t('admin:auditLogs.export.truncated'));
    } catch (err) {
      showErrorToast(err);
    } finally {
      setExporting(false);
    }
  };

  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'medium' });

  const columns: DataTableColumn<AuditLogItem>[] = [
    { id: 'date', header: t('admin:auditLogs.columns.date'), cell: (r) => <span className="whitespace-nowrap tabular-nums">{fmt(r.created_date)}</span> },
    {
      id: 'action', header: t('admin:auditLogs.columns.action'),
      cell: (r) => (
        <span className="flex flex-col">
          <Badge variant={DESTRUCTIVE.test(r.action) ? 'destructive' : 'outline'} className="w-fit">{actionLabel(t, r.action)}</Badge>
          <span className="font-mono text-[11px] text-muted-foreground">{r.action}</span>
        </span>
      ),
    },
    {
      id: 'actor', header: t('admin:auditLogs.columns.actor'),
      cell: (r) => (
        <span className="flex flex-col">
          <span>{r.actor_email || t('admin:auditLogs.system')}</span>
          {r.actor_organisation_id && <span className="text-xs text-muted-foreground">{orgName.get(r.actor_organisation_id) ?? t('admin:auditLogs.unknownOrganisation')}</span>}
        </span>
      ),
    },
    { id: 'target', header: t('admin:auditLogs.columns.target'), cell: (r) => <TargetCell row={r} orgName={orgName} /> },
    { id: 'ip', header: t('admin:auditLogs.columns.ip'), hideable: true, defaultHidden: true, cell: (r) => <span className="font-mono text-xs">{r.ip || '—'}</span> },
    { id: 'detail', header: t('admin:auditLogs.columns.detail'), cell: (r) => <AuditDetail row={r} /> },
  ];

  const pills: FilterPill[] = [
    ...(action ? [{ id: 'action', label: actionLabel(t, action), onRemove: () => setParam('action', '') }] : []),
    ...(actor ? [{ id: 'actor', label: `« ${actor} »`, onRemove: () => { setActorDraft(''); setParam('actor', ''); } }] : []),
    ...(organisationId ? [{ id: 'org', label: orgName.get(organisationId) ?? organisationId, onRemove: () => setParam('organisation_id', '') }] : []),
  ];
  const clearFilters = () => { setActorDraft(''); setParams(new URLSearchParams(period === '30d' ? {} : { period }), { replace: true }); };

  const err = query.error;
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      <PageHeader
        as="h1"
        title={t('admin:auditLogs.title')}
        subtitle={t('admin:auditLogs.subtitle')}
        actions={
          <Button variant="outline" className="gap-2" onClick={() => void exportCsv()} disabled={exporting}>
            {exporting ? <Loader2 className="size-4 animate-spin motion-reduce:animate-none" /> : <Download className="size-4" />}
            {t('admin:auditLogs.export.button')}
          </Button>
        }
      />

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" role="group" aria-label={t('admin:auditLogs.facets')}>
        <Select value={action || ALL} onValueChange={(v) => setParam('action', v === ALL ? '' : v)}>
          <SelectTrigger aria-label={t('admin:auditLogs.columns.action')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('admin:auditLogs.allActions')}</SelectItem>
            {(actions.data ?? []).map((a) => (
              <SelectItem key={a.action} value={a.action}>{actionLabel(t, a.action)} ({a.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="search"
          value={actorDraft}
          onChange={(e) => setActorDraft(e.target.value)}
          placeholder={t('admin:auditLogs.actorPlaceholder')}
          aria-label={t('admin:auditLogs.columns.actor')}
        />
        <Select value={organisationId || ALL} onValueChange={(v) => setParam('organisation_id', v === ALL ? '' : v)}>
          <SelectTrigger aria-label={t('admin:auditLogs.organisation')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('admin:auditLogs.allOrganisations')}</SelectItem>
            {(orgs.data?.organisations ?? []).map((o) => <SelectItem key={o.id} value={o.id}>{o.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={(v) => { setNow(Date.now()); setParam('period', v === '30d' ? '' : v); }}>
          <SelectTrigger aria-label={t('admin:auditLogs.period.label')}><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => <SelectItem key={p} value={p}>{t(`admin:auditLogs.period.${p}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        {period === 'custom' && (
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <Input type="date" className="w-auto" value={customFrom} max={customTo || undefined} onChange={(e) => setParam('from', e.target.value)} aria-label={t('admin:auditLogs.period.from')} />
            <span aria-hidden>→</span>
            <Input type="date" className="w-auto" value={customTo} min={customFrom || undefined} onChange={(e) => setParam('to', e.target.value)} aria-label={t('admin:auditLogs.period.to')} />
          </div>
        )}
      </div>

      <DataTable<AuditLogItem>
        caption={t('admin:auditLogs.title')}
        storageKey="admin-audit-logs"
        columns={columns}
        rows={query.data?.items ?? []}
        getRowId={(r) => r.id}
        status={query.isLoading ? 'loading' : query.isError && !query.data ? 'error' : 'ready'}
        error={err ? { message: toUserMessage(err), code: err instanceof ApiErrorException ? err.code : undefined, onRetry: () => void query.refetch() } : undefined}
        filters={pills}
        onClearFilters={clearFilters}
        empty={{ title: t('admin:auditLogs.empty') }}
        pagination={{ page, pageSize: PAGE_SIZE, total: query.data?.total ?? null, onPageChange }}
      />
    </div>
  );
}

function TargetCell({ row, orgName }: { row: AuditLogItem; orgName: Map<string, string> }) {
  const { t } = useTranslation(['admin']);
  const metaOrg = typeof row.metadata.organisationId === 'string' ? row.metadata.organisationId : null;
  const label = row.target_type === 'Organisation'
    ? orgName.get(row.target_id) ?? row.target_label
    : row.target_label && !/^[a-f0-9]{24}$/i.test(row.target_label) ? row.target_label : '';
  return (
    <span className="flex flex-col">
      <span>{label || (row.target_type ? t(`admin:auditLogs.targetTypes.${row.target_type}`, { defaultValue: row.target_type }) : '—')}</span>
      {metaOrg && metaOrg !== row.target_id && <span className="text-xs text-muted-foreground">{orgName.get(metaOrg) ?? t('admin:auditLogs.unknownOrganisation')}</span>}
    </span>
  );
}

/** before/after pairs a route records: { before, after } objects, or previousRole/newRole. */
export function diffOf(metadata: Record<string, unknown>): Array<{ key: string; before: unknown; after: unknown }> {
  const out: Array<{ key: string; before: unknown; after: unknown }> = [];
  const b = metadata.before; const a = metadata.after;
  if (b && a && typeof b === 'object' && typeof a === 'object') {
    const keys = new Set([...Object.keys(b as object), ...Object.keys(a as object)]);
    for (const k of keys) {
      const bv = (b as Record<string, unknown>)[k]; const av = (a as Record<string, unknown>)[k];
      if (JSON.stringify(bv) !== JSON.stringify(av)) out.push({ key: k, before: bv, after: av });
    }
  }
  if ('previousRole' in metadata || 'newRole' in metadata) out.push({ key: 'role', before: metadata.previousRole, after: metadata.newRole });
  return out;
}

const show = (v: unknown) => (v === undefined || v === null || v === '' ? '—' : typeof v === 'string' ? v : JSON.stringify(v));

function AuditDetail({ row }: { row: AuditLogItem }) {
  const { t } = useTranslation(['admin']);
  const diff = diffOf(row.metadata);
  const rest = Object.entries(row.metadata).filter(([k]) => !['before', 'after', 'previousRole', 'newRole'].includes(k));
  return (
    <details className="group text-xs">
      <summary className="inline-flex min-h-11 cursor-pointer items-center text-[var(--vf-accent)] hover:underline">
        {diff.length ? t('admin:auditLogs.detail.showDiff', { count: diff.length }) : t('admin:auditLogs.detail.show')}
      </summary>
      <div className="mt-1 space-y-2 rounded border p-2" style={{ borderColor: 'var(--vf-border)' }}>
        {diff.length > 0 && (
          <table className="w-full">
            <thead><tr className="text-left text-muted-foreground"><th className="pr-2 font-medium">{t('admin:auditLogs.detail.field')}</th><th className="pr-2 font-medium">{t('admin:auditLogs.detail.before')}</th><th className="font-medium">{t('admin:auditLogs.detail.after')}</th></tr></thead>
            <tbody>
              {diff.map((d) => (
                <tr key={d.key}>
                  <td className="pr-2 font-mono">{d.key}</td>
                  <td className="pr-2"><del className="text-red-700 dark:text-red-400">{show(d.before)}</del></td>
                  <td><ins className="text-emerald-700 no-underline dark:text-emerald-400">{show(d.after)}</ins></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rest.length > 0 && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
            {rest.map(([k, v]) => (<Fragment key={k}><dt className="font-mono text-muted-foreground">{k}</dt><dd className="break-all">{show(v)}</dd></Fragment>))}
          </dl>
        )}
        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 border-t pt-2 font-mono text-[11px] text-muted-foreground" style={{ borderColor: 'var(--vf-border)' }}>
          <dt>{t('admin:auditLogs.detail.actorId')}</dt><dd className="break-all">{row.actor_user_id || '—'}</dd>
          <dt>{t('admin:auditLogs.detail.target')}</dt><dd className="break-all">{[row.target_type, row.target_id].filter(Boolean).join(' ') || '—'}</dd>
          <dt>IP</dt><dd>{row.ip || '—'}</dd>
          <dt>{t('admin:auditLogs.detail.userAgent')}</dt><dd className="break-all">{row.user_agent || '—'}</dd>
        </dl>
      </div>
    </details>
  );
}
